import './picker-theme.css';
import { abortSearch, loadAssets, scheduleSearch } from './search.js';
import { pickerPrices } from './picker-prices.js';

const TAB_LABELS = { all: 'همه', coin: 'ارز دیجیتال', fiat: 'فیات', commodity: 'طلا و فلزات' };

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  })[character]);
}

function assetMarkup(asset, activeTab, state) {
  const name = escapeHtml(asset.fa_name || asset.name);
  const slug = encodeURIComponent(String(asset.slug));
  const source = `https://cdn.arz.digital/cr-odin/img/assets/${slug}/64x64.png`;
  const image = state.imageMarkup ? state.imageMarkup(source, asset.fa_name || asset.name)
    : `<img width="36" height="36" src="${source}" alt="${name}">`;
  return `<button type="button" class="arz-converter-asset-option" data-converter-asset="${slug}" data-converter-asset-type="${escapeHtml(asset.type)}">
    <span class="arz-converter-asset-option__identity">
      ${image}
      <span class="arz-converter-asset-option__name"><span>${name}</span><span>${escapeHtml(asset.symbol)}</span></span>
    </span>
    ${pickerPrices(asset)}
  </button>`;
}

function createDialog(state) {
  const dialog = document.createElement('div');
  dialog.className = 'arz-modal arz-modal-center arz-modal-fade arz-converter-asset-dialog';
  dialog.dataset.converterDialog = '';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-label', 'انتخاب ارز');
  dialog.hidden = true;
  dialog.innerHTML = `<div class="arz-modal-content arz-modal-content-totop arz-converter-asset-dialog__sheet">
    <div class="arz-converter-asset-dialog__header">
      <label class="arz-converter-asset-dialog__search">
        <span class="screen-reader-text">جستجوی ارز</span>
        <input name="converter-search" placeholder="جستجوی ارز" autocomplete="off" data-converter-search dir="rtl">
        <span class="arz-tw-hidden arz-tw-absolute arz-tw-inset-y-0 arz-tw-left-3 arz-tw-items-center arz-tw-pointer-events-none" data-converter-search-loading><span class="arz-tw-size-5 arz-spiner-loading-container"><span class="arz-spiner-loading"></span></span></span>
      </label>
      <button type="button" class="arz-modal-close arz-converter-asset-dialog__close" data-converter-close aria-label="بستن">×</button>
    </div>
    <div class="arz-converter-asset-dialog__tabs" role="tablist" aria-label="نوع دارایی" data-converter-tabs>
      ${Object.entries(TAB_LABELS).map(([key, label]) => `<button type="button" role="tab" class="search-modal-tabs" data-converter-tab="${key}">${label}</button>`).join('')}
    </div>
    <div class="arz-converter-asset-dialog__results" data-converter-results role="listbox" aria-label="نتایج انتخاب ارز"></div>
  </div>`;
  document.body.append(dialog);
  return dialog;
}

export function renderResults(state, assets, status) {
  const container = state.dialog.querySelector('[data-converter-results]');
  if (status === 'loading') return;
  const otherSlug = state.selectedSide === 'base' ? state.quote.slug : state.base.slug;
  const previous = state.dialogResults;
  if (previous?.assets === assets && previous.otherSlug === otherSlug
      && previous.tab === state.activeTab && previous.status === status) return;
  state.dialogResults = { assets, otherSlug, tab: state.activeTab, status };
  if (status === 'error') {
    container.innerHTML = '<p class="arz-tw-p-6 arz-tw-text-center arz-tw-text-rose-500">دریافت فهرست ارزها ناموفق بود. دوباره تلاش کنید.</p>';
    return;
  }
  const filtered = assets.filter((asset) => asset.slug !== otherSlug);
  container.innerHTML = filtered.length
    ? filtered.map((asset) => assetMarkup(asset, state.activeTab, state)).join('')
    : '<p class="arz-tw-p-6 arz-tw-text-center arz-tw-text-slate-500">نتیجه‌ای پیدا نشد.</p>';
  state.onImagesRendered?.();
}

function setActiveTab(state, tab) {
  state.activeTab = tab;
  state.dialog.querySelectorAll('[data-converter-tab]').forEach((button) => {
    const selected = button.dataset.converterTab === tab;
    button.setAttribute('aria-selected', String(selected));
    button.classList.toggle('arz-tw-text-primary', selected);
    button.classList.toggle('arz-tw-border-primary', selected);
  });
}

export function closeModal(state) {
  if (!state.dialog || state.dialog.hidden) return;
  abortSearch(state);
  state.dialog.style.display = 'none';
  state.dialog.hidden = true;
  state.dialog.classList.remove('arz-modal-show');
  state.restoreFocus?.focus({ preventScroll: true });
  // Reuse default rows on the next opening instead of rebuilding them in a tap.
  requestAnimationFrame(() => window.setTimeout(() => {
    if (!state.dialog.hidden) return;
    // Restore scrolling after the dismissal has painted, without forcing page
    // layout in the close handler. A reopened picker must retain its scroll lock.
    document.body.classList.remove('arz-modal-open');
    setActiveTab(state, 'all');
    const cached = state.defaultCache.get('all:');
    if (cached) renderResults(state, cached, 'ready');
  }, 0));
}

export function openModal(state, side, onSelect) {
  prepareModal(state, onSelect);
  abortSearch(state);
  state.selectedSide = side;
  state.restoreFocus = state.root.querySelector(`[data-ac-side-type-calc="${side}"]`);
  setActiveTab(state, 'all');
  const input = state.dialog.querySelector('[data-converter-search]');
  input.value = '';
  const paint = (assets, status) => renderResults(state, assets, status);
  const cached = state.defaultCache.get('all:');
  // Render cached defaults while hidden, before focus forces visible layout.
  if (cached) paint(cached, 'ready');
  else {
    state.dialog.querySelector('[data-converter-results]').textContent = '';
    state.dialogResults = null;
  }
  state.dialog.hidden = false;
  state.dialog.style.display = 'block';
  state.dialog.classList.add('arz-modal-show');
  state.onImagesRendered?.();
  document.body.classList.add('arz-modal-open');
  // Synchronous focus preserves the mobile keyboard's user activation.
  input.focus({ preventScroll: true });

  if (!cached) {
    const requestId = state.searchRequestId;
    requestAnimationFrame(() => window.setTimeout(() => {
      // Closing, typing, switching tabs or reopening invalidates this work.
      if (state.dialog.hidden || requestId !== state.searchRequestId) return;
      loadAssets(state, '', 'all', paint);
    }, 0));
  }
}

export function prepareModal(state, onSelect) {
  if (state.dialog) return;
  state.dialog = createDialog(state);
  const input = state.dialog.querySelector('[data-converter-search]');
  const paint = (assets, status) => renderResults(state, assets, status);
  state.dialog.dataset.bound = '1';
  state.dialog.addEventListener('click', (event) => {
    const close = event.target.closest('[data-converter-close]');
    if (close) {
      event.stopPropagation();
      return closeModal(state);
    }
    const tab = event.target.closest('[data-converter-tab]');
    if (tab) {
      abortSearch(state);
      setActiveTab(state, tab.dataset.converterTab);
      input.value = '';
      loadAssets(state, '', state.activeTab, paint);
      return;
    }
    const assetButton = event.target.closest('[data-converter-asset]');
    if (assetButton) {
      const key = `${state.activeTab}:`;
      const pool = [...state.defaultCache.values(), ...state.searchCache.values()].flat();
      const slug = decodeURIComponent(assetButton.dataset.converterAsset);
      const asset = pool.find((item) => item.slug === slug && item.type === assetButton.dataset.converterAssetType);
      if (asset) {
        onSelect(asset);
        closeModal(state);
      } else if (state.defaultCache.has(key)) {
        renderResults(state, [], 'error');
      }
    }
  });
  input.addEventListener('input', () => scheduleSearch(state, input.value, paint));
  state.dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') return closeModal(state);
    if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      const selector = document.activeElement?.matches('[data-converter-tab]') ? '[data-converter-tab]' : '[data-converter-asset]';
      const items = [...state.dialog.querySelectorAll(selector)];
      const index = items.indexOf(document.activeElement);
      if (index !== -1 && items.length) {
        event.preventDefault();
        const forwards = event.key === 'ArrowDown' || event.key === 'ArrowLeft';
        items[(index + (forwards ? 1 : -1) + items.length) % items.length].focus();
        return;
      }
    }
    if (event.key !== 'Tab') return;
    const focusable = [...state.dialog.querySelectorAll('button:not([disabled]), input:not([disabled])')].filter((node) => !node.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
}
